import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { actions as assistantActions } from 'features/assistant';
import { useControlPanel } from './useControlPanel';
import { convertAssistantData } from './convertAssistantData';

const useAssistant = () => {
  const dispatch = useDispatch();
  const { actionProcessing, parsedMessage } = useSelector(state => state.assistant, shallowEqual);
  const {
    inputState: {
      currentSections,
      currentPeriods,
      currentSubsections,
      handleSelectCurrentPeriod,
      handleSelectCurrentSection,
      handleSelectCurrentSubsection,
      backToPreviousSection,
      currentDate,
      handleSelectFromDate,
      handleSelectToDate,
      handleOpenDate,
      handleInputClarificationValue,
      applyParsedResult,
    },
    outputState: {
      fullContext,
      isTableShown,
      isSectionSelected,
      isPeriodSelected,
      isSubsectionSelected,
      isBackButtonActive,
      isBalanceMode,
      selectedSection,
      selectedPeriod,
      selectedSubsection,
      inputClarificationValue,
      parsedUsername,
      isEmptyParseResult,
    },
  } = useControlPanel();

  const [values, setValues] = useState({
    isDirect: true,
    isFullStructure: false,
  });

  const handleSubmit = localValues => {
    if (!selectedSubsection && !isBalanceMode) return;
    const isDateSelected = selectedPeriod && selectedPeriod.value === currentDate.value;
    const action = isBalanceMode ? selectedSection.value : selectedSubsection.value;

    dispatch(
      assistantActions.getTableData(
        convertAssistantData({
          ...(localValues ?? values),
          period: isDateSelected ? currentDate : selectedPeriod,
          action,
          username: parsedUsername,
        }),
      ),
    );
  };

  const sendClarification = async () => {
    if (!inputClarificationValue) return;
    const parsed = await dispatch(assistantActions.parseText(inputClarificationValue));
    handleInputClarificationValue('');
    if (!parsed) return;

    dispatch(assistantActions.clearTableData());

    if (!parsed.action) {
      applyParsedResult(parsed);
      return;
    }

    const isDirect = parsed.scope !== 'full_structure';
    setValues({ isDirect, isFullStructure: !isDirect });
    applyParsedResult(parsed);
  };

  const closeAssistant = () => {
    dispatch(assistantActions.handleAssistantModal(false));
    dispatch(assistantActions.setParsedMessage(''));
    dispatch(assistantActions.clearTableData());
  };

  const handleChangeStructure = (isDirect = false) => {
    setValues(prev => {
      const next = { isDirect: !prev.isDirect, isFullStructure: !prev.isFullStructure };
      if (isTableShown) handleSubmit(next);
      return next;
    });
  };

  useEffect(() => {
    if (!isTableShown) return;
    if (isBalanceMode || fullContext.length === 3) handleSubmit();
  }, [isTableShown, selectedSection?.value, selectedSubsection?.value, currentDate, parsedUsername]);

  return {
    inputState: {
      values,
      setValues,
      currentDate,
      currentSections,
      currentPeriods,
      currentSubsections,
      handleSelectCurrentPeriod,
      handleSelectCurrentSection,
      handleSelectCurrentSubsection,
      handleChangeStructure,
      handleSelectFromDate,
      handleSelectToDate,
      handleOpenDate,
      backToPreviousSection,
      closeAssistant,
      handleSubmit,
      sendClarification,
      handleInputClarificationValue,
    },
    outputState: {
      fullContext,
      isTableShown,
      isSectionSelected,
      isPeriodSelected,
      isSubsectionSelected,
      isBackButtonActive,
      actionProcessing,
      inputClarificationValue,
      parsedMessage,
      parsedUsername,
      isEmptyParseResult,
    },
  };
};

export default useAssistant;
